import { BottomSheet } from "@App/Components/BottomSheet";
import { ContactUs } from "@App/Components/ContactUs";
import { MapIcon, PriceComparisonIcon } from "@App/Components/common/Icons";
import {
  AvailableDateFilter,
  BrandFilter,
  DistanceFilter,
  GarageNameFilter,
  LocationFilter,
  PremiumServiceFilter,
} from "@App/Components/Filters";
import { GarageList } from "@App/Components/GarageList";
import { MobileFilter } from "@App/Components/MobileFilter";
import { SortListBox } from "@App/Components/SortListBox";
import { actionCreators, type State } from "@App/state";
import type { Garage } from "@App/types";
import { bindActionCreators } from "@reduxjs/toolkit";
import {
  Fragment,
  type FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import "react-calendar/dist/Calendar.css";
import { FreeVehicleReturnHint } from "@App/Components/ClientSpecific/MeinAuto/FreeVehicleReturnHint";
import { ContactPartner } from "@App/Components/ContactPartner";
import { FilterOverview } from "@App/Components/Filters/FilterOverview";
import { ManufacturerFilter } from "@App/Components/Filters/ManufacturerFilter";
import { PremiumServiceFilterNoRequiredInput } from "@App/Components/Filters/PremiumServiceFilterNoRequiredInput";
import { PremiumServiceFilterSingularInput } from "@App/Components/Filters/PremiumServiceFilterSingularInput";
import { FilterList } from "@App/Components/molecules/FilterList";
import { FeedbackHint } from "@App/Components/Partials/FeedbackHint";
import { ServiceFAQs } from "@App/Components/ServiceFAQs";
import { GaragePropertyContext } from "@App/contexts/listing/GaragePropertyContext";
import { useGarages } from "@App/hooks/garageList/useGarages";
import { useResultsAnalytics } from "@App/hooks/useResultAnalytics";
import { useScrollToTop } from "@App/hooks/useScrollToTop";
import { useServiceFunnel } from "@App/hooks/useServiceFunnel";
import { useFreeVehicleReturn } from "@App/hooks/whitelabel/meinAuto/useFreeVehicleReturn";
import { Filters, SortOrderType } from "@App/types/enums";
import { hasItems } from "@App/utils/array";
import {
  OrganizationIsArvalAndServiceIsWheelChange,
  OrganizationIsNotAkfAndNotAkfLangzeitmiete,
  OrganizationIsNotArvalOrServiceIsNotWheelChange,
  ServiceIsWheelChangeAndOrganizationIsNio,
  ServiceIsWheelChangeTiresstoredAndOrganizationIsNio,
  ServiceIsWheelReplacementAndOrganizationIsNio,
} from "@App/utils/conditionals/combinations";
import {
  OrganizationIsAthlon,
  OrganizationIsALD,
  OrganizationIsDBConnect,
  OrganizationIsAyvens,
  OrganizationIsMobilityConcept,
  OrganizationIsRepareo,
} from "@App/utils/conditionals/organization";
import {
  ServiceHasCustomSearchHeadline,
  ServiceIsInspection,
  ServiceIsWheelChange,
  ServiceShouldDisplayReviews,
} from "@App/utils/conditionals/services";
import { getConfig } from "@App/utils/getConfig";
import { useTranslation } from "react-i18next";
import BackButton from "../../Components/BackButton";

export type GaragesProps = {
  embedded?: boolean;
  onSubmit?: (garage: Garage) => void;
};

export const Garages: FunctionComponent<GaragesProps> = ({
  embedded = false,
  onSubmit,
}: GaragesProps) => {
  const { availableGarageProperties, availableFilterItems } = useContext(GaragePropertyContext);
  const { t } = useTranslation();
  useScrollToTop();

  const [currentlyShownFilter, setCurrentlyShownFilter] = useState(Filters.None);
  const [toggleMap, setToggleMap] = useState(false);
  const [hasPremiumServices, setHasPremiumServices] = useState(false);
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null);

  const dispatch = useDispatch();
  const { chooseGarage, changeSortByFilter, chooseSlot, setGarageProperties } = bindActionCreators(
    actionCreators,
    dispatch,
  );

  const service = useSelector((state: State) => state.orders.service);
  const motorization = useSelector((state: State) => state.orders.motorization);
  const premiumServices = useSelector((state: State) => state.orders.premiumServices);
  const generation = useSelector((state: State) => state.orders.generation);
  const brand = useSelector((state: State) => state.orders.brand);
  const organization = useSelector((state: State) => state.orders.organization);
  const place = useSelector((state: State) => state.orders.place);

  // Filters
  const sortBy = useSelector((state: State) => state.filters.sortBy);
  const selectedGarageProperties = useSelector((state: State) => state.filters.garageProperties);
  const selectedPremiumServices = useSelector((state: State) => state.orders.premiumServices);
  const bevSupport = useSelector((state: State) => state.filters.bevSupport);
  const commercialVehicleSupport = useSelector(
    (state: State) => state.filters.commercialVehicleSupport,
  );
  const distance = useSelector((state: State) => state.filters.distance);

  const { serviceSlug } = useParams();
  useServiceFunnel(serviceSlug ?? "");

  const {
    loading,
    fetchMoreLoading,
    garages,
    recommendedGarages,
    paginatorInfo,
    isFiltered,
    earliestAvailableGarage,
    bestReviewedGarage,
    nearestGarage,
    authorizedGarage,
    error,
    refetch,
    loadMore,
  } = useGarages();

  // remove previously chosen slots
  useEffect(() => {
    chooseSlot(null);
  }, []);

  useEffect(() => {
    // change standard sorting to recommended if service is inspection
    if (
      !!service &&
      OrganizationIsRepareo(organization) &&
      ["inspektion", "bremsenwechsel"].includes(service.slug)
    ) {
      changeSortByFilter(SortOrderType.RECOMMENDED);
    } else if (ServiceIsWheelChange(service)) {
      changeSortByFilter(SortOrderType.SMALLEST_DISTANCE);
    }
  }, [service, organization]);

  useEffect(() => {
    if (loading && loadingStartTime === null) {
      setLoadingStartTime(Date.now());
    }
    if (!loading && loadingStartTime !== null) {
      setLoadingStartTime(null);
    }
  }, [loading, loadingStartTime]);

  // Pass data to PostHog
  useResultsAnalytics({
    loading,
    garages,
    loadingStartTime,
    distance,
    generation,
    brand,
    service,
    organization,
    sortBy,
    motorization,
    premiumServices,
  });

  const selectGarage = (garage: Garage) => {
    chooseGarage(garage);
    onSubmit?.(garage);
  };

  const changeFilter = (type: Filters) => {
    setCurrentlyShownFilter(type);
  };

  const closeFilters = () => {
    setCurrentlyShownFilter(Filters.None);
  };

  const onChangeGarage = useCallback(
    (garage: Garage) => {
      selectGarage(garage);
    },
    [chooseGarage, onSubmit],
  );

  const onChangeSorting = useCallback(
    (sortBy: SortOrderType) => {
      changeSortByFilter(sortBy);
    },
    [changeSortByFilter],
  );

  const { isFreeVehicleReturnFilterToggled, showFreeVehicleReturnHint, vehicleReturnHintOnClick } =
    useFreeVehicleReturn({
      availableGarageProperties,
      selectedGarageProperties,
      availableFilterItems,
      setGarageProperties,
      service,
      organization,
    });

  // disable the location fetch on load
  const config = getConfig(organization);
  const disableFetchOnLoad = config.garageList?.disableFetchLocationOnLoad;

  return (
    <>
      <div className="bg-neutral-100 py-5">
        <div className="container">
          <div className="mb-4 md:hidden">
            <div className="text-lg font-bold font-serif mb-3">
              {service && [132, 94].includes(+service.id)
                ? t("Pruefstellen in Ihrer Naehe", {
                    service: service.pivot.display_name ?? service.name,
                  })
                : t("Werkstaetten in Ihrer Naehe", {
                    service: service?.pivot.display_name ?? service?.name,
                  })}
            </div>
            <MobileFilter
              filterPlaceOpen={currentlyShownFilter === Filters.Place}
              filterServiceOpen={currentlyShownFilter === Filters.Service}
              filterDateOpen={currentlyShownFilter === Filters.Date}
              filterBrandOpen={currentlyShownFilter === Filters.Brand}
              onChangeMap={setToggleMap}
              onChange={changeFilter}
              hasPremiumServices={hasPremiumServices}
            />
            <BackButton />
          </div>
          <div className="hidden md:flex md:justify-between md:items-center">
            <div className="flex items-center">
              <div className="mr-4">
                <PriceComparisonIcon />
              </div>
              <div>
                <div className="text-2xl font-serif">
                  {service && ServiceHasCustomSearchHeadline(service) && (
                    <>
                      {service?.pivot?.display_search_headline?.replace(
                        "%ORGANIZATION%",
                        organization?.name ?? "",
                      )}
                    </>
                  )}
                  {service &&
                    [132, 94].includes(+service.id) &&
                    !ServiceHasCustomSearchHeadline(service) && (
                      <>
                        {t("Partnerpruefstellen in Ihrer Naehe", {
                          organization: organization.name,
                        })}
                      </>
                    )}
                  {service &&
                    ![132, 94].includes(+service.id) &&
                    !ServiceHasCustomSearchHeadline(service) && (
                      <>
                        {t("Partnerwerkstaetten in Ihrer Naehe", {
                          organization: organization.name,
                        })}
                      </>
                    )}
                </div>
              </div>
            </div>
            <div className="flex items-center">
              <div className={`flex items-center ${toggleMap ? "hidden" : ""}`}>
                <div className="font-bold text-sm mr-4">{t("sort by")}</div>
                <div className="w-52 mr-4">
                  <SortListBox
                    selected={sortBy}
                    onChange={onChangeSorting}
                    displayReviewFilter={(service && ServiceShouldDisplayReviews(service)) || false}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => setToggleMap(!toggleMap)}
                className="cursor-pointer mt-1"
              >
                <div className="flex items-center rounded shadow bg-white py-2 px-3">
                  <div className="mr-2">
                    <MapIcon />
                  </div>
                  <div>{toggleMap ? t("Listenansicht") : t("Kartenansicht")}</div>
                </div>
              </button>
            </div>
          </div>

          <div className="md:flex md:pt-10">
            <div className="hidden md:block w-[318px]">
              <BackButton />
              {service && (
                <div className="flex flex-col justify-center mr-12 max-w-[270px] text-neutral-700">
                  {!OrganizationIsRepareo(organization) && !OrganizationIsAyvens(organization) && (
                    <div className="mb-8">
                      <FilterOverview
                        organization={organization}
                        service={service}
                        premiumServices={selectedPremiumServices}
                        brand={brand}
                        bevSupport={bevSupport}
                        commercialVehicleSupport={commercialVehicleSupport}
                      />
                    </div>
                  )}
                  {organization.settings?.garage_list.filters.includes("search") && (
                    <div className="mb-8">
                      <div className="relative">
                        <GarageNameFilter />
                      </div>
                    </div>
                  )}
                  {organization.settings?.garage_list.filters.includes("brand") && !brand && (
                    <div className="mb-8">
                      <div className="font-bold text-sm mb-3">{t("Marke")}</div>
                      <div>
                        <BrandFilter />
                      </div>
                    </div>
                  )}
                  {organization.settings?.garage_list.filters.includes("place") && (
                    <Fragment>
                      <div className="mb-8">
                        <div className="font-bold text-sm mb-3">{t("Umgebung")}</div>
                        <div>
                          <LocationFilter disableFetchOnLoad={disableFetchOnLoad} />
                        </div>
                      </div>
                      {place && ( // only show distance if we have a location
                        <div className="mb-8">
                          <DistanceFilter />
                        </div>
                      )}
                    </Fragment>
                  )}
                  {ServiceIsInspection(service) && OrganizationIsMobilityConcept(organization) && (
                    <div className="mb-8">
                      <ManufacturerFilter />
                    </div>
                  )}

                  {organization.settings?.garage_list.filters.includes("date") && (
                    <div className="mb-8">
                      <div className="font-bold text-sm mb-3">{t("Verfuegbarkeit")}</div>
                      <AvailableDateFilter />
                    </div>
                  )}
                  {!ServiceIsWheelChangeAndOrganizationIsNio(organization, service) &&
                    !ServiceIsWheelReplacementAndOrganizationIsNio(organization, service) &&
                    !ServiceIsWheelChangeTiresstoredAndOrganizationIsNio(organization, service) &&
                    !OrganizationIsAthlon(organization) && (
                      <div className="mb-8">
                        <PremiumServiceFilter
                          organization={organization}
                          service={service}
                          premiumServices={selectedPremiumServices}
                          onLoaded={(hasItems) => setHasPremiumServices(hasItems)}
                        />
                      </div>
                    )}
                  {(ServiceIsWheelChangeAndOrganizationIsNio(organization, service) ||
                    ServiceIsWheelChangeTiresstoredAndOrganizationIsNio(organization, service)) && (
                    <div className="mb-8">
                      <PremiumServiceFilterNoRequiredInput
                        organization={organization}
                        service={service}
                        premiumServices={selectedPremiumServices}
                        onLoaded={(hasItems) => setHasPremiumServices(hasItems)}
                      />
                    </div>
                  )}
                  {(ServiceIsWheelReplacementAndOrganizationIsNio(organization, service) ||
                    OrganizationIsAthlon(organization)) && (
                    <div className="mb-8">
                      <PremiumServiceFilterSingularInput
                        organization={organization}
                        service={service}
                        premiumServices={selectedPremiumServices}
                        onLoaded={(hasItems) => setHasPremiumServices(hasItems)}
                      />
                    </div>
                  )}
                  {hasItems(availableFilterItems) && (
                    <div className="mb-8">
                      <div className="font-bold text-sm mb-3">{t("Filter")}</div>
                      <FilterList
                        availableItems={availableFilterItems}
                        selectedItems={selectedGarageProperties}
                        onChange={setGarageProperties}
                      />
                    </div>
                  )}
                  {service && <ServiceFAQs sidebar />}
                </div>
              )}
            </div>
            <div className={`flex flex-col items-center w-full`}>
              {showFreeVehicleReturnHint && (
                <FreeVehicleReturnHint
                  isFreeVehicleReturnToggled={isFreeVehicleReturnFilterToggled}
                  onClick={vehicleReturnHintOnClick}
                />
              )}
              <GarageList
                service={service}
                onChange={onChangeGarage}
                toggleMap={toggleMap}
                onToggleMap={setToggleMap}
                mobileServiceFAQs
                useGarages={{
                  loading,
                  fetchMoreLoading,
                  garages,
                  recommendedGarages,
                  paginatorInfo,
                  isFiltered,
                  earliestAvailableGarage,
                  bestReviewedGarage,
                  nearestGarage,
                  authorizedGarage,
                  error,
                  refetch,
                  loadMore,
                }}
              />
            </div>
          </div>
          {!embedded && OrganizationIsNotAkfAndNotAkfLangzeitmiete(organization) && (
            <div className="md:grid md:grid-cols-[50%_50%] md:pt-20 md:mb-20">
              {!OrganizationIsALD(organization) && (
                <div className="pt-12 md:pt-0 md:min-w-1/2 md:pr-16">
                  {OrganizationIsDBConnect(organization) && (
                    <ContactPartner
                      organization={organization}
                      partnerLogo={{
                        url: organization?.settings?.navbar?.partnerLogo.url ?? "",
                        alt: t("layout.navbar.partnerLogo.alt"),
                      }}
                    />
                  )}
                  {!OrganizationIsDBConnect(organization) && <FeedbackHint />}
                </div>
              )}

              <div className="pt-5 md:pt-0 md:pl-16">
                <ContactUs
                  titleHidden={OrganizationIsDBConnect(organization)}
                  hidePartner={true}
                  imageHidden={OrganizationIsALD(organization)}
                />
              </div>
              {OrganizationIsDBConnect(organization) && (
                <div className="pt-5 md:pt-0">
                  <FeedbackHint />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <BottomSheet
        title={t("Umgebung")}
        open={currentlyShownFilter === Filters.Place}
        onConfirm={() => closeFilters()}
        onClose={() => closeFilters()}
      >
        <p>{t("proximity-search-text")}</p>
        <LocationFilter disableFetchOnLoad={disableFetchOnLoad} className={"mt-8"} />
        {place && ( // only show distance filter if we have a location
          <div className={"mt-6"}>
            <DistanceFilter />
          </div>
        )}
      </BottomSheet>
      <BottomSheet
        title={t("Datum")}
        open={currentlyShownFilter === Filters.Date}
        onConfirm={() => closeFilters()}
        onClose={() => closeFilters()}
      >
        <AvailableDateFilter show={true} />
      </BottomSheet>
      {hasPremiumServices &&
        OrganizationIsNotArvalOrServiceIsNotWheelChange(organization, service) && (
          <BottomSheet
            title={t("Zusatzservices")}
            open={currentlyShownFilter === Filters.Service}
            onConfirm={() => closeFilters()}
            onClose={() => closeFilters()}
          >
            <PremiumServiceFilter
              organization={organization}
              service={service}
              premiumServices={selectedPremiumServices}
            />
            {hasItems(availableFilterItems) && (
              <FilterList
                availableItems={availableFilterItems}
                selectedItems={selectedGarageProperties}
                onChange={setGarageProperties}
              />
            )}
          </BottomSheet>
        )}
      {service && OrganizationIsArvalAndServiceIsWheelChange(organization, service) && (
        <BottomSheet
          title={"Filter"}
          open={currentlyShownFilter === Filters.Service}
          onConfirm={() => closeFilters()}
          onClose={() => closeFilters()}
        >
          {hasPremiumServices && (
            <div className="font-bold text-sm mb-3 mt-4">{t("Zusatzservices")}</div>
          )}
          <PremiumServiceFilter
            organization={organization}
            service={service}
            premiumServices={selectedPremiumServices}
          />
          {hasItems(availableFilterItems) && (
            <FilterList
              availableItems={availableFilterItems}
              selectedItems={selectedGarageProperties}
              onChange={setGarageProperties}
            />
          )}
        </BottomSheet>
      )}
      <BottomSheet
        title={t("Werkstattname suchen")}
        open={currentlyShownFilter === Filters.Search}
        onConfirm={() => closeFilters()}
        onClose={() => closeFilters()}
      >
        <GarageNameFilter />
      </BottomSheet>
      <BottomSheet
        title={"Marke suchen"}
        open={currentlyShownFilter === Filters.Brand}
        onConfirm={() => closeFilters()}
        onClose={() => closeFilters()}
      >
        <div className="mb-24">
          <BrandFilter />
        </div>
      </BottomSheet>
    </>
  );
};
